import { useEffect, useRef, useState } from 'react'
import { Field, SectionHeading } from './components'
import { TranslatedHtml, useI18n } from './i18n'
import { signupPageTitle } from './pageTitle'
import { StepAnimation } from './StepAnimation'
import type {
  AccountResponse,
  AccountField,
  BillingOption,
  ExistingAccountLinks,
  OAuthHandoff,
  ProUpsellOption,
  ProUpsellResponse,
  PurchaseResponse,
  SignupConfiguration,
  SignupFormState,
} from './types'

type FormUpdater = <K extends keyof SignupFormState>(
  key: K,
  value: SignupFormState[K],
) => void

function formatPrice(option: BillingOption, languageCode = 'en'): string {
  return new Intl.NumberFormat(languageCode.replace('_', '-'), {
    style: 'currency',
    currency: option.currency || 'USD',
    minimumFractionDigits: option.priceCents % 100 === 0 ? 0 : 2,
  }).format(option.priceCents / 100)
}

function isEmailValid(emailAddress: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailAddress)
}

function StepIntroduction({
  flowTitle,
  current,
  total,
  title,
  description,
}: {
  flowTitle: string
  current: number
  total: number
  title: string
  description: string
}) {
  const { t } = useI18n()

  return (
    <>
      <SectionHeading eyebrow={`${flowTitle} | ${t('signup.step', current, total)}`}>
        {title}
      </SectionHeading>
      <p className="step-description">{description}</p>
    </>
  )
}

interface BundleStepProps {
  configuration: SignupConfiguration
  form: SignupFormState
  update: FormUpdater
  totalSteps: number
  emailError: string
  existingAccount: ExistingAccountLinks | null
}

function ExistingAccountOptions({ links }: { links: ExistingAccountLinks }) {
  const { t } = useI18n()
  const options = [
    {
      key: 'login',
      href: links.loginUrl,
      title: t('signup.existing.login'),
      note: t('signup.existing.login.note'),
      icon: <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />,
    },
    {
      key: 'manage',
      href: links.manageAccountUrl,
      title: t('signup.existing.manage'),
      note: t('signup.existing.manage.note'),
      icon: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    },
    ...(links.upgradeUrl
      ? [{
          key: 'upgrade',
          href: links.upgradeUrl,
          title: t('signup.existing.upgrade'),
          note: t('signup.existing.upgrade.note'),
          icon: <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6-4.5-4.2 6.1-.7z" />,
        }]
      : []),
  ]

  return (
    <div className="existing-account field-full" data-testid="existing-account-options">
      <p className="existing-account-heading">{t('signup.existing.heading')}</p>
      <div className="existing-account-list">
        {options.map((option) => (
          <a
            key={option.key}
            className={`existing-account-option existing-account-${option.key}`}
            href={option.href}
          >
            <span className="existing-account-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">{option.icon}</svg>
            </span>
            <span className="existing-account-copy">
              <strong>{option.title}</strong>
              <span>{option.note}</span>
            </span>
            <span className="existing-account-arrow" aria-hidden="true">→</span>
          </a>
        ))}
      </div>
    </div>
  )
}

export function BundleStep({
  configuration,
  form,
  update,
  totalSteps,
  emailError,
  existingAccount,
}: BundleStepProps) {
  const { languageCode, t } = useI18n()
  const recommendedLabel = languageCode === 'es'
    ? 'Recomendado'
    : languageCode === 'fr'
      ? 'Recommandé'
      : 'Recommended'
  return (
    <div className="step-content" data-testid="bundle-step">
      <StepAnimation name="bundle" />
      <StepIntroduction
        flowTitle={signupPageTitle(configuration.brand)}
        current={1}
        total={totalSteps}
        title={t('signup.bundle.heading')}
        description={t('signup.bundle.subtitle')}
      />

      <div className="choice-list" role="radiogroup" aria-label={t('signup.tl.bundle')}>
        {configuration.billingOptions.map((option) => (
          <label
            key={option.id}
            className={`choice-card ${option.recommended ? 'recommended' : ''} ${form.packageId === option.id ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="packageId"
              value={option.id}
              checked={form.packageId === option.id}
              onChange={() => update('packageId', option.id)}
            />
            <span className="radio-mark" aria-hidden="true" />
            <span className="choice-copy">
              <span className="choice-title-line">
                <strong>{option.name}</strong>
                {option.recommended && (
                  <span className="badge">{recommendedLabel}</span>
                )}
              </span>
              <span className="choice-description">{option.description}</span>
            </span>
            <span className="choice-price">
              <strong>{formatPrice(option, languageCode)}</strong>
              <span>{option.cycleLabel}</span>
            </span>
          </label>
        ))}
      </div>

      {configuration.billingOptions.length === 0 && (
        <div className="empty-state">
          No billing packages are currently available for this signup.
        </div>
      )}

      {form.packageId !== null && (
        <div
          className="revealed-account-section"
          id="bundle-account-section"
        >
          <hr />
          <h2>{t('signup.account.title')}</h2>
          <div className="form-grid">
            <Field
              id="bundle-email"
              label={t('common.emailaddress')}
              type="email"
              value={form.emailAddress}
              onChange={(value) => update('emailAddress', value)}
              placeholder="you@example.com"
              autoComplete="email"
              maxLength={50}
              error={
                emailError || (form.emailAddress && !isEmailValid(form.emailAddress)
                  ? t('signup.personal.email.invalid')
                  : undefined)
              }
              required
              full
            />
            {existingAccount && <ExistingAccountOptions links={existingAccount} />}
            <Field
              id="bundle-password"
              label={t('signup.password.choose')}
              type="password"
              value={form.password}
              onChange={(value) => update('password', value)}
              placeholder={t('signup.password.choose.placeholder')}
              autoComplete="new-password"
              error={
                form.password && form.password.length < 6
                  ? t('signup.password.choose.invalid')
                  : undefined
              }
              required
            />
            <Field
              id="bundle-confirm-password"
              label={t('signup.password.confirm')}
              type="password"
              value={form.confirmPassword}
              onChange={(value) => update('confirmPassword', value)}
              placeholder={t('signup.password.confirm.placeholder')}
              autoComplete="new-password"
              error={
                form.confirmPassword && form.password !== form.confirmPassword
                  ? t('signup.password.confirm.invalid')
                  : undefined
              }
              required
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface PurchaseStepProps {
  configuration: SignupConfiguration
  form: SignupFormState
  update: FormUpdater
  selectedPackage?: BillingOption
  totalSteps: number
  purchaseError: PurchaseResponse | null
}

export function PurchaseStep({
  configuration,
  form,
  update,
  selectedPackage,
  totalSteps,
  purchaseError,
}: PurchaseStepProps) {
  const { languageCode, t } = useI18n()
  const states = configuration.stateProvinces.filter(
    (stateProvince) => stateProvince.countryId === form.countryId,
  )
  const showPurchaseErrors = form.termsAccepted
  const cardDigits = form.cardNumber.replace(/\D/g, '')

  return (
    <div className="step-content" data-testid="purchase-step">
      <StepAnimation
        name={purchaseError ? 'fail' : 'purchase'}
      />
      <StepIntroduction
        flowTitle={signupPageTitle(configuration.brand)}
        current={2}
        total={totalSteps}
        title={t('signup.purchase.heading')}
        description={t('signup.purchase.subtitle')}
      />

      {configuration.features.sandboxMode && (
        <div className="purchase-alert purchase-alert-sandbox" role="status">
          <strong>Sandbox mode</strong>
          <span>
            Payment details will be submitted to the gateway in test mode.
            No iDecide account, database transaction, email, or promotion will
            be created.
          </span>
        </div>
      )}

      {purchaseError && (
        <div
          className={`purchase-alert purchase-alert-error ${purchaseError.retryAllowed ? '' : 'purchase-alert-blocked'}`}
          id="purchase-error-alert"
          role="alert"
          tabIndex={-1}
        >
          <strong>
            {purchaseError.paymentReceived
              ? 'Payment received — account setup needs attention'
              : purchaseError.retryAllowed
                ? 'Purchase not completed'
                : 'Purchase status needs review'}
          </strong>
          <span>{purchaseError.message}</span>
          {Object.keys(purchaseError.fieldErrors).length > 0 && (
            <ul>
              {Object.entries(purchaseError.fieldErrors).map(([field, message]) => (
                <li key={field}>{message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selectedPackage && (
        <div className="order-summary">
          <div>
            <span>
              <strong>{selectedPackage.name}</strong>{' '}
              <span className="muted">({selectedPackage.cycleLabel})</span>
            </span>
            <strong>{formatPrice(selectedPackage, languageCode)}</strong>
          </div>
          <div className="order-total">
            <strong>{t('signup.summary.duetoday')}</strong>
            <strong>{formatPrice(selectedPackage, languageCode)}</strong>
          </div>
          <small>{t('signup.summary.guarantee')}</small>
        </div>
      )}

      <h2>{t('signup.personal.title')}</h2>
      <div className="form-grid">
        <Field
          id="first-name"
          label={t('signup.personal.firstname')}
          value={form.firstName}
          onChange={(value) => update('firstName', value)}
          autoComplete="given-name"
          maxLength={50}
          error={
            showPurchaseErrors && !form.firstName.trim()
              ? t('signup.personal.firstname.invalid')
              : undefined
          }
          required
        />
        <Field
          id="last-name"
          label={t('signup.personal.lastname')}
          value={form.lastName}
          onChange={(value) => update('lastName', value)}
          autoComplete="family-name"
          maxLength={50}
          error={
            showPurchaseErrors && !form.lastName.trim()
              ? t('signup.personal.lastname.invalid')
              : undefined
          }
          required
        />
        <Field
          id="purchase-email"
          label={t('common.emailaddress')}
          type="email"
          value={form.emailAddress}
          onChange={() => undefined}
          readOnly
          required
        />
        <Field
          id="phone-number"
          label={t('signup.personal.phone')}
          type="tel"
          value={form.phoneNumber}
          onChange={(value) => update('phoneNumber', value)}
          autoComplete="tel"
          maxLength={50}
        />
      </div>

      <hr />
      <h2>{t('signup.purchase.payment.section')}</h2>
      <div className="form-grid">
        <Field
          id="card-name"
          label={t('signup.creditcard.name')}
          value={form.cardName}
          onChange={(value) => update('cardName', value)}
          autoComplete="cc-name"
          maxLength={50}
          error={
            showPurchaseErrors && !form.cardName.trim()
              ? t('signup.creditcard.name.invalid')
              : undefined
          }
          required
          full
        />
        <Field
          id="card-number"
          label={t('signup.creditcard.number')}
          value={form.cardNumber}
          onChange={(value) => update('cardNumber', value)}
          placeholder="4242 4242 4242 4242"
          autoComplete="cc-number"
          maxLength={19}
          error={
            showPurchaseErrors && cardDigits.length < 15
              ? t('signup.creditcard.number.invalid')
              : undefined
          }
          required
          full
        />
        <Field
          id="card-expiry"
          label={t('signup.creditcard.expiry')}
          value={form.cardExpiry}
          onChange={(value) => update('cardExpiry', value)}
          placeholder="MM/YY"
          autoComplete="cc-exp"
          maxLength={5}
          error={
            showPurchaseErrors && !/^\d{2}\/\d{2}$/.test(form.cardExpiry)
              ? t('signup.creditcard.expiry.invalid')
              : undefined
          }
          required
        />
        <Field
          id="card-code"
          label={t('signup.creditcard.cvs')}
          value={form.cardCode}
          onChange={(value) => update('cardCode', value)}
          placeholder="CVC"
          autoComplete="cc-csc"
          maxLength={4}
          error={
            showPurchaseErrors && form.cardCode.length < 3
              ? t('signup.creditcard.cvs.invalid')
              : undefined
          }
          required
        />
      </div>

      <hr />
      <h2>{t('signup.creditcard.billingaddress')}</h2>
      <div className="form-grid">
        <Field
          id="address-line-1"
          label={t('signup.personal.address1')}
          value={form.addressLine1}
          onChange={(value) => update('addressLine1', value)}
          autoComplete="address-line1"
          maxLength={50}
          error={
            showPurchaseErrors && !form.addressLine1.trim()
              ? t('signup.personal.address1.invalid')
              : undefined
          }
          required
          full
        />
        <Field
          id="address-line-2"
          label={t('signup.personal.address2')}
          value={form.addressLine2}
          onChange={(value) => update('addressLine2', value)}
          autoComplete="address-line2"
          maxLength={50}
          full
        />
        <Field
          id="city"
          label={t('signup.personal.city')}
          value={form.city}
          onChange={(value) => update('city', value)}
          autoComplete="address-level2"
          maxLength={50}
          error={
            showPurchaseErrors && !form.city.trim()
              ? t('signup.personal.city.invalid')
              : undefined
          }
          required
        />
        <label
          className={`field ${showPurchaseErrors && !form.countryId ? 'field-error' : ''}`}
          htmlFor="country"
        >
          <span className="field-label">
            {t('signup.personal.country')}
            <span className="required-mark" aria-hidden="true">*</span>
          </span>
          <span className="input-shell">
            <select
              id="country"
              value={form.countryId ?? ''}
              required
              aria-invalid={showPurchaseErrors && !form.countryId}
              aria-describedby={
                showPurchaseErrors && !form.countryId
                  ? 'country-error'
                  : undefined
              }
              onChange={(event) => {
                update('countryId', Number(event.target.value))
                update('stateProvinceId', null)
              }}
            >
              <option value="" disabled>
                {t('signup.choose.placeholder')}
              </option>
              {configuration.countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </span>
          {showPurchaseErrors && !form.countryId && (
            <span className="field-error-message" id="country-error" role="alert">
              {t('signup.choose')}
            </span>
          )}
        </label>
        <label
          className={`field ${showPurchaseErrors && states.length > 0 && !form.stateProvinceId ? 'field-error' : ''}`}
          htmlFor="state-province"
        >
          <span className="field-label">
            {t('signup.personal.state')}
            {states.length > 0 && (
              <span className="required-mark" aria-hidden="true">*</span>
            )}
          </span>
          <span className="input-shell">
            <select
              id="state-province"
              value={form.stateProvinceId ?? ''}
              required={states.length > 0}
              aria-invalid={
                showPurchaseErrors && states.length > 0 && !form.stateProvinceId
              }
              aria-describedby={
                showPurchaseErrors && states.length > 0 && !form.stateProvinceId
                  ? 'state-province-error'
                  : undefined
              }
              onChange={(event) =>
                update('stateProvinceId', Number(event.target.value))
              }
            >
              <option value="" disabled>
                {t('signup.choose.placeholder')}
              </option>
              {states.map((stateProvince) => (
                <option key={stateProvince.id} value={stateProvince.id}>
                  {stateProvince.name}
                </option>
              ))}
            </select>
          </span>
          {showPurchaseErrors && states.length > 0 && !form.stateProvinceId && (
            <span
              className="field-error-message"
              id="state-province-error"
              role="alert"
            >
              {t('signup.choose')}
            </span>
          )}
        </label>
        <Field
          id="postal-zip"
          label={t('signup.personal.postalzip')}
          value={form.postalZip}
          onChange={(value) => update('postalZip', value)}
          maxLength={10}
          autoComplete="postal-code"
          error={
            showPurchaseErrors && !form.postalZip.trim()
              ? t('signup.personal.postalzip.invalid')
              : undefined
          }
          required
        />
      </div>

      <label className="terms-check">
        <input
          type="checkbox"
          checked={form.termsAccepted}
          onChange={(event) => update('termsAccepted', event.target.checked)}
        />
        <TranslatedHtml messageKey="signup.account.terms.agree" />
      </label>
    </div>
  )
}

interface AccountStepProps {
  configuration: SignupConfiguration
  form: SignupFormState
  update: FormUpdater
  credentialsEditing: boolean
  onEditCredentials: () => void
  onCancelCredentials: () => Promise<void>
  onSaveCredentials: (
    emailAddress: string,
    password: string,
    confirmPassword: string,
  ) => Promise<AccountResponse>
  submissionError: string
  fieldErrors: Record<string, string>
  totalSteps: number
}

export function AccountStep({
  configuration,
  form,
  update,
  credentialsEditing,
  onEditCredentials,
  onCancelCredentials,
  onSaveCredentials,
  submissionError,
  fieldErrors,
  totalSteps,
}: AccountStepProps) {
  const { t } = useI18n()
  const [credentialDraft, setCredentialDraft] = useState(() => ({
    emailAddress: form.emailAddress,
    password: form.password,
    confirmPassword: form.confirmPassword,
  }))
  const [credentialValidationRequested, setCredentialValidationRequested] =
    useState(false)
  const [credentialSaving, setCredentialSaving] = useState(false)
  const [credentialError, setCredentialError] = useState('')
  const credentialErrorRef = useRef<HTMLParagraphElement>(null)
  const [credentialFieldErrors, setCredentialFieldErrors] =
    useState<Record<string, string>>({})

  useEffect(() => {
    if (credentialError) {
      credentialErrorRef.current?.focus({ preventScroll: true })
      credentialErrorRef.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [credentialError])

  function updateAccountField(field: AccountField, value: string) {
    update('accountFields', {
      ...form.accountFields,
      [field.key]: value,
    })
  }

  function beginCredentialEdit() {
    setCredentialDraft({
      emailAddress: form.emailAddress,
      password: form.password,
      confirmPassword: form.confirmPassword,
    })
    setCredentialValidationRequested(false)
    setCredentialError('')
    setCredentialFieldErrors({})
    onEditCredentials()
  }

  async function saveCredentials() {
    if (credentialSaving)
      return
    const valid = isEmailValid(credentialDraft.emailAddress)
      && credentialDraft.password.length >= 6
      && credentialDraft.password === credentialDraft.confirmPassword

    if (!valid) {
      setCredentialValidationRequested(true)
      return
    }

    setCredentialSaving(true)
    setCredentialError('')
    setCredentialFieldErrors({})
    try {
      const response = await onSaveCredentials(
        credentialDraft.emailAddress,
        credentialDraft.password,
        credentialDraft.confirmPassword,
      )
      if (response.status !== 'SUCCESS') {
        setCredentialError(response.message)
        setCredentialFieldErrors(response.fieldErrors)
      }
    }
    catch (error) {
      setCredentialError(
        error instanceof Error
          ? error.message
          : t('signup.account.error.saveFailed'),
      )
    }
    finally {
      setCredentialSaving(false)
    }
  }

  async function cancelCredentials() {
    if (credentialSaving)
      return
    setCredentialSaving(true)
    setCredentialError('')
    try {
      await onCancelCredentials()
      setCredentialDraft({ emailAddress: '', password: '', confirmPassword: '' })
      setCredentialFieldErrors({})
      setCredentialValidationRequested(false)
    }
    catch (error) {
      setCredentialError(error instanceof Error ? error.message : t('signup.account.error.cancelFailed'))
    }
    finally {
      setCredentialSaving(false)
    }
  }

  return (
    <div className="step-content" data-testid="account-step">
      <StepAnimation name="success" swapTo="account" />
      <SectionHeading
        eyebrow={`${signupPageTitle(configuration.brand)} | ${t('signup.step', 3, totalSteps)}`}
        centered
      >
        {t('signup.addons.completed.heading')}
      </SectionHeading>
      <p className="step-description centered">
        {t('signup.addons.completed.note')}
      </p>

      {submissionError && (
        <div className="purchase-alert purchase-alert-error" role="alert">
          <strong>Account setup was not completed</strong>
          <span>{submissionError}</span>
        </div>
      )}

      <h2>{t('signup.account.title')}</h2>
      {credentialsEditing ? (
        <div
          className="credential-editor"
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent.isComposing
                || !(event.target instanceof HTMLInputElement))
              return

            event.preventDefault()
            if (!credentialSaving)
              void saveCredentials()
          }}
        >
          <div className="form-grid">
            <Field
              id="account-email"
              label={t('common.emailaddress')}
              type="email"
              value={credentialDraft.emailAddress}
              maxLength={50}
              onChange={(value) => setCredentialDraft((current) => ({
                ...current,
                emailAddress: value,
              }))}
              error={
                credentialFieldErrors.emailAddress
                || (credentialValidationRequested
                  && !isEmailValid(credentialDraft.emailAddress)
                  ? t('signup.personal.email.invalid')
                  : undefined)
              }
              required
              full
            />
            <Field
              id="account-password"
              label={t('common.password')}
              type="password"
              value={credentialDraft.password}
              onChange={(value) => setCredentialDraft((current) => ({
                ...current,
                password: value,
              }))}
              error={
                credentialFieldErrors.password
                || (credentialValidationRequested
                  && credentialDraft.password.length < 6
                  ? t('signup.password.choose.invalid')
                  : undefined)
              }
              required
            />
            <Field
              id="account-confirm-password"
              label={t('signup.password.confirm')}
              type="password"
              value={credentialDraft.confirmPassword}
              onChange={(value) => setCredentialDraft((current) => ({
                ...current,
                confirmPassword: value,
              }))}
              error={
                credentialFieldErrors.confirmPassword
                || (credentialValidationRequested
                  && credentialDraft.password !== credentialDraft.confirmPassword
                  ? t('signup.password.confirm.invalid')
                  : undefined)
              }
              required
            />
          </div>
          <div className="credential-actions">
            <button
              type="button"
              className="button button-soft credential-save"
              disabled={credentialSaving}
              onClick={() => void saveCredentials()}
            >
              {credentialSaving ? t('signup.purchase.processing') : t('signup.save')}
            </button>
            <button
              type="button"
              className="button button-soft credential-cancel"
              disabled={credentialSaving}
              onClick={() => void cancelCredentials()}
            >
              {t('signup.account.cancelChanges')}
            </button>
          </div>
          {credentialError && (
            <p className="inline-action-error" role="alert" ref={credentialErrorRef} tabIndex={-1}>
              {credentialError}
            </p>
          )}
        </div>
      ) : (
        <div className="credential-summary">
          <div>
            <span>
              <small>{t('common.emailaddress')}</small>
              <strong>{form.emailAddress}</strong>
            </span>
            <span>
              <small>{t('common.password')}</small>
              <strong>••••••••</strong>
            </span>
          </div>
          <button
            type="button"
            className="button button-soft"
            onClick={beginCredentialEdit}
          >
            {t('signup.change')}
          </button>
        </div>
      )}

      <hr />
      <h2>{t('signup.package.kicker', configuration.brand.name)}</h2>
      <div className="form-grid">
        {configuration.accountFields.map((field) => (
          <Field
            key={field.key}
            id={`account-${field.key}`}
            label={field.label}
            type={field.type}
            value={form.accountFields[field.key] ?? ''}
            onChange={(value) => updateAccountField(field, value)}
            prefix={field.prefix}
            suffix={field.suffix}
            placeholder={field.placeholder}
            required={field.required}
            maxLength={field.maximumLength}
            hint={field.hint}
            error={fieldErrors[field.key]}
            full
          />
        ))}
      </div>
      {configuration.brand.presentationName && (
        <TranslatedHtml
          as="div"
          className="info-callout"
          messageKey="signup.businessinfo.note"
          values={[
            configuration.brand.presentationName,
            configuration.brand.presentationName,
          ]}
        />
      )}

      <hr />
      <label className="field field-full" htmlFor="marketing-source">
        <span className="field-label">
          {t('signup.profile.source')}
          <span className="optional"> {t('signup.optional')}</span>
        </span>
        <span className="input-shell">
          <select
            id="marketing-source"
            value={form.marketingSourceId ?? ''}
            onChange={(event) =>
              update(
                'marketingSourceId',
                event.target.value ? Number(event.target.value) : null,
              )
            }
          >
            <option value="">{t('signup.choose.placeholder')}</option>
            {configuration.marketingSources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.title}
              </option>
            ))}
          </select>
        </span>
        {fieldErrors.marketingSourceId && (
          <span className="field-error-message" role="alert">
            {fieldErrors.marketingSourceId}
          </span>
        )}
      </label>
    </div>
  )
}

export function ProUpsellStep({
  configuration,
  form,
  update,
  totalSteps,
  selectedPackage,
  proUpsell,
  submissionError,
}: {
  configuration: SignupConfiguration
  form: SignupFormState
  update: FormUpdater
  totalSteps: number
  selectedPackage: BillingOption
  proUpsell: ProUpsellOption
  submissionError: ProUpsellResponse | null
}) {
  const { languageCode, t } = useI18n()
  const priceDifference = Math.max(
    0,
    proUpsell.priceCents - selectedPackage.priceCents,
  )
  const formattedDifference = new Intl.NumberFormat(
    languageCode.replace('_', '-'),
    {
      style: 'currency',
      currency: proUpsell.currency || 'USD',
      minimumFractionDigits: priceDifference % 100 === 0 ? 0 : 2,
    },
  ).format(priceDifference / 100)

  return (
    <div className="step-content" data-testid="addons-pro-step">
      <StepAnimation name="add-on" />
      <StepIntroduction
        flowTitle={signupPageTitle(configuration.brand)}
        current={4}
        total={totalSteps}
        title={t('signup.addon.heading')}
        description={t('signup.addons.promo.pro.body')}
      />
      {configuration.features.sandboxMode && (
        <div className="purchase-alert purchase-alert-sandbox" role="status">
          <strong>Sandbox mode</strong>
          <span>
            Selecting this upgrade submits a second test payment. No package,
            transaction, or email record will be created.
          </span>
        </div>
      )}
      {submissionError && (
        <div className="purchase-alert purchase-alert-error" role="alert">
          <strong>Pro upgrade not completed</strong>
          <span>{submissionError.message}</span>
        </div>
      )}
      <label
        className={`addon-card ${form.addonSelected ? 'selected' : ''}`}
      >
        <input
          type="checkbox"
          checked={form.addonSelected}
          onChange={(event) => update('addonSelected', event.target.checked)}
        />
        <span className="radio-mark" aria-hidden="true" />
        <span>
          <span className="choice-title-line">
            <strong>{proUpsell.name}</strong>
            <span className="badge">Send &amp; Create</span>
          </span>
          <span className="choice-description">
            {proUpsell.description || 'Everything in your bundle, plus the full myiDecide builder platform.'}
          </span>
          <ul>
            <li>Build unlimited AI-driven interactive presentations</li>
            <li>Drag-and-drop builder, templates, and PowerPoint import</li>
            <li>Advanced analytics with Zapier integration</li>
            <li>15-minute session with a Customer Success Coach</li>
          </ul>
        </span>
        <span className="choice-price">
          <strong>+{formattedDifference}</strong>
          <span>one-time prorated upgrade</span>
        </span>
      </label>
    </div>
  )
}

export function DoneStep({
  configuration,
  form,
  selectedPackage,
  purchaseResponse,
  proUpsell,
  proUpsellResponse,
  oauthHandoff,
  onRestart,
  resendAvailableInSeconds,
  resendMessage,
  resendSubmitting,
  onResend,
}: {
  configuration: SignupConfiguration
  form: SignupFormState
  selectedPackage?: BillingOption
  purchaseResponse: PurchaseResponse | null
  proUpsell: ProUpsellOption | null | undefined
  proUpsellResponse: ProUpsellResponse | null
  oauthHandoff: OAuthHandoff | null
  onRestart: () => void
  resendAvailableInSeconds: number
  resendMessage: string
  resendSubmitting: boolean
  onResend: () => void
}) {
  const { t } = useI18n()
  const simulated = configuration.features.previewMode
    || purchaseResponse?.simulated === true
  const testPaymentProcessed =
    purchaseResponse?.testPaymentProcessed === true

  return (
    <div className="step-content done-step" data-testid="done-step">
      <StepAnimation name="success" />
      <SectionHeading centered>
        {simulated
          ? 'Sandbox test complete'
          : t('signup.done.heading', form.firstName ? `, ${form.firstName}` : '')}
      </SectionHeading>
      {simulated ? (
        <p className="step-description centered">
          The signup flow completed safely. No iDecide records or outbound
          messages were created.
        </p>
      ) : (
        <TranslatedHtml
          as="p"
          className="step-description centered"
          messageKey="signup.done.emailNotice"
          values={[form.emailAddress]}
        />
      )}

      {!simulated && (
        <>
          <button
            className="button button-soft resend-email"
            type="button"
            disabled={resendSubmitting || resendAvailableInSeconds > 0}
            onClick={onResend}
          >
            {resendAvailableInSeconds > 0
              ? t('signup.done.resend.wait', resendAvailableInSeconds)
              : t('signup.done.resend')}
          </button>
          {resendMessage && (
            <p className="resend-message" role="status">{resendMessage}</p>
          )}
        </>
      )}

      <div className="completion-list">
        {simulated ? (
          <>
            <div>
              <span>✓</span>
              <p>
                {testPaymentProcessed
                  ? <>Test payment approved — reference #{purchaseResponse?.orderId}</>
                  : 'Payment submission safely simulated'}
              </p>
            </div>
            <div>
              <span>✓</span>
              <p>Account and database transaction creation skipped</p>
            </div>
            <div>
              <span>✓</span>
              <p>Email and external promotion integrations skipped</p>
            </div>
            {proUpsellResponse?.status === 'SUCCESS' && (
              <div>
                <span>✓</span>
                <p>
                  Pro upgrade test completed
                  {proUpsellResponse.orderId
                    ? <> — reference #{proUpsellResponse.orderId}</>
                    : ''}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <span>✓</span>
              <p>
                <strong>{selectedPackage?.name ?? 'Selected bundle'}</strong>{' '}
                purchased — order #{purchaseResponse?.orderId || 'PENDING'}
              </p>
            </div>
            {proUpsellResponse?.status === 'SUCCESS' && (
              <div>
                <span>✓</span>
                <p>
                  Upgraded to <strong>{proUpsell?.name ?? 'iDecide Pro'}</strong>
                  {proUpsellResponse.orderId
                    ? <> — order #{proUpsellResponse.orderId}</>
                    : ''}
                </p>
              </div>
            )}
            <div>
              <span>✓</span>
              <p>Login created</p>
            </div>
            {configuration.brand.presentationName && (
              <div>
                <span>✓</span>
                <p>
                  Access to the{' '}
                  <strong>{configuration.brand.presentationName}</strong>
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="done-actions">
        {oauthHandoff
          ? (
            // a form rather than a link, so the email address reaches the login page without appearing in a URL
            <form method="post" action={oauthHandoff.continueUrl}>
              <input type="hidden" name="email" value={form.emailAddress} />
              <button className="button button-primary" type="submit">
                {t('signup.done.continueOAuth', oauthHandoff.clientName)} →
              </button>
            </form>
          )
          : (
            <a
              className="button button-primary"
              href={configuration.links.loginUrl}
            >
              {t('common.login', 'iDecide')} →
            </a>
          )}
        {!simulated && (
          <button
            className="button button-link"
            type="button"
            onClick={onRestart}
          >
            {t('signup.done.anotherPurchase')}
          </button>
        )}
        {simulated && (
          <button
            className="button button-link"
            type="button"
            onClick={onRestart}
          >
            {t('signup.done.restart')}
          </button>
        )}
      </div>
    </div>
  )
}
